import { useMutation } from "@apollo/client"
import CustomButton from "components/Atoms/CustomButton"
import CustomDateInput from "components/Atoms/CustomDateInput"
import StringInput from "components/Atoms/StringInput"
import { BlockUserNote } from "components/Common/BlockUserNote"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FileUpload from "components/Molecules/FileUpload"
import FormController from "components/Molecules/FormController"
import FormLayout from "components/Molecules/FormLayout"
import MutationOptions from "components/Organisms/MutationOptions"
import VendorUserDetails from "components/Templates/form/VendorUserDetails"
import { EDUCATION_TYPES, GENDER_TYPES } from "data/static.items"
import {
  DC_SURVEY_AREAS,
  DISTRIBUTION_CENTERS,
  GROUPS,
  KYCS,
  ROUTES,
  ROUTE_CATEGORIES,
} from "graphql/selector.queries.gql"
import {
  ADD_UPDATE_USER_CREDIT,
  ADD_UPDATE_VENDOR,
} from "graphql/vendor/vendor.mutations.gql"
import { VENDOR_DETAIL, VENDOR_LIST } from "graphql/vendor/vendor.queries.gql"
import { useFormDisabled } from "hooks/useAuth"
import { useAuthorizationSimple } from "hooks/useAuthorizationStrict"
import useErrors from "hooks/useErrors"
import useRouteForm from "hooks/useRouteForm"
import { cloneDeep, omit, pick, set } from "lodash-es"
import vendorModel from "models/vendor.model"
import React, { useEffect, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ErrorToast, SuccessToast } from "utils/AppToaster"
import { getId } from "utils/custom.utils"
import {
  DocumentTypes,
  isInValidDocumentPresent,
  isValidDocument,
} from "utils/documentHelper"
import { remove } from "utils/ds.utils"
import OutletsMap from "./OutletsMap"
import { AppPermissions } from "constants/permissions"

type VendorRegistrationProps = {
  onComplete?: any
  defaultGroup?: any
}
const VendorRegistration = ({
  onComplete,
  defaultGroup,
}: VendorRegistrationProps) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [state, setState, { refetch }] = useRouteForm(VENDOR_DETAIL, {
    default: {},
    fetchPolicy: "network-only",
    transform(state) {
      const creditSet0 = state?.creditSet?.[0]
      return {
        ...state,
        businessName: state?.shopsManaged?.[0]?.name,
        gender: GENDER_TYPES.find(gt => gt.id === state.gender),
        education: EDUCATION_TYPES.find(et => et.id === state.education),
        routes: state.routepoints?.map(point => point.route),
        credit: creditSet0
          ? {
              id: creditSet0.id,
              totalAmount: creditSet0.totalAmount?.toFixed(2),
              remainingAmount: creditSet0.remainingAmount?.toFixed(2),
              usedAmount: creditSet0.usedAmount?.toFixed(2),
              paymentCycleDays: creditSet0.paymentCycleDays,
              startDate: creditSet0.startDate,
              endDate: creditSet0.endDate,
              isActive: creditSet0.isActive,
              status: creditSet0.status,
            }
          : undefined,
      }
    },
  })

  const [saveCredit] = useMutation(ADD_UPDATE_USER_CREDIT)

  async function saveCreditIfNeeded(userId: string) {
    const credit = state?.credit
    if (!credit?.totalAmount) return
    await saveCredit({
      variables: {
        id: credit?.id,
        userId,
        totalAmount: parseFloat(credit.totalAmount),
        paymentCycleDays: credit.paymentCycleDays
          ? parseInt(credit.paymentCycleDays)
          : undefined,
        startDate: credit.startDate ?? undefined,
        endDate: credit.endDate ?? undefined,
        isActive: credit.isActive ?? true,
        status: credit.status ?? "ACT",
      },
    })
  }

  useEffect(() => {
    if (defaultGroup) {
      setState(val => ({ ...val, groups: [defaultGroup] }))
    }
  }, [defaultGroup])

  function handleChange(path, value) {
    const stateCopy = cloneDeep(state)
    if (path === "routeCategory" || path === "surveyArea") {
      set(stateCopy, "routes", [])
    }
    if (path === "distributionCenter") {
      set(stateCopy, "surveyArea", null)
    }

    set(stateCopy, path, value)

    // Auto-recalculate remaining amount whenever the credit limit changes
    if (path === "credit.totalAmount") {
      const newLimit = parseFloat(value) || 0
      const usedAmount = parseFloat(stateCopy?.credit?.usedAmount) || 0
      const remaining = Math.max(newLimit - usedAmount, 0)
      set(stateCopy, "credit.remainingAmount", remaining)
    }

    setState(stateCopy)
  }

  const parsedVariables = useMemo(() => {
    const kyc = state?.kycDetails?.map(kyc => {
      return {
        id: kyc?.id,
        idNumber: kyc?.idNumber,
        photo: kyc?.photo,
        kycType: kyc?.kycType?.id,
      }
    })
    return {
      ...pick(state, [
        "id",
        "firstName",
        "lastName",
        "number",
        "email",
        "dateOfBirth",
        "username",
        "businessName",
      ]),
      businessId: state?.shopsManaged?.[0]?.id,
      address: omit(state?.address, ["__typename"]),
      groups: state?.groups?.map(getId),
      education: state.education?.enum,
      gender: state.gender?.enum,
      kycDetail: kyc,
      routeIds: state.routes?.map(route => route.id),
      deleteKycIds: state?.removeKycIds ?? [],
    }
  }, [state])

  const [errors] = useErrors({ schema: vendorModel, state: parsedVariables })
  const disabled = useFormDisabled("vendor", parsedVariables)
  const canChangeAddress = useAuthorizationSimple(
    AppPermissions.canEditVendorAddress
  )
  return (
    <>
      <FormLayout id={id} type="Vendor">
        <FormController
          errors={errors?.details}
          source={state}
          onChange={handleChange}
          disabled={disabled || state?.isBlocked}
          disableAll={true}
        >
          {state?.id && (
            <>
              <div className="flex items-baseline">
                <h5 className="mr-6 text-2xl">
                  <span className="text-muted">Vendor - </span> {state.fullName}{" "}
                  <span className="text-muted">
                    ( {state.customerId || "NA"} )
                  </span>
                </h5>
              </div>
              <hr className="mt-0 mb-3" />
              {state?.isBlocked ? (
                <BlockUserNote remark={state?.blockRemark} />
              ) : null}
            </>
          )}
          <VendorUserDetails disableAddress={!canChangeAddress} />
          <div className="grid grid-cols-12 gap-4 mt-3">
            <div className="col-span-6">
              <DynamicMultiSelector
                required
                path="groups"
                label="Vendor Types"
                fill
                big
                query={GROUPS}
                variables={{ category: "RTL" }}
              />
            </div>
            <div className="col-span-3">
              <CustomDateInput
                fill={true}
                label="Date Of Birth"
                path="dateOfBirth"
                helperText="eg. 27/06/1985"
              ></CustomDateInput>
            </div>
          </div>
          {id !== "new" && (
            <div>
              <h6 className="mt-6 vt-section-header">Credit Details</h6>
              <hr className="mt-0" />
              <div className="grid grid-cols-12 gap-4 mt-3">
                <div className="col-span-3">
                  <StringInput
                    path="credit.totalAmount"
                    label="Credit Limit"
                    type="number"
                  />
                </div>
                <div className="col-span-3">
                  <StringInput
                    path="credit.remainingAmount"
                    label="Credit Remaining"
                    type="number"
                    disabled
                  />
                </div>
                <div className="col-span-3">
                  <StringInput
                    path="credit.usedAmount"
                    label="Credit Used"
                    type="number"
                    disabled
                  />
                </div>
                <div className="col-span-3">
                  <StringInput
                    path="credit.paymentCycleDays"
                    label="Payment Cycle (Days)"
                    type="number"
                  />
                </div>
              </div>
            </div>
          )}

          <h6 className="mt-6 vt-section-header">
            KYC Details{" "}
            <CustomButton
              icon="plus"
              intent="success"
              minimal
              onClick={e => {
                setState(prev => ({
                  ...prev,
                  kycDetails: prev?.kycDetails?.concat({}),
                }))
              }}
            />
          </h6>
          <hr className="mt-0" />
          {state?.kycDetails?.map((d, idx) => {
            const pan = state?.kycDetails?.find(doc => {
              return doc?.kycType?.name === DocumentTypes.PAN
            })?.idNumber
            return (
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-3">
                  <DynamicSelector
                    big
                    query={KYCS}
                    path={`kycDetails.${idx}.kycType`}
                    label="Type"
                    helperText="eg. Aadhar"
                  />
                </div>
                <div className="col-span-3">
                  <StringInput
                    path={`kycDetails.${idx}.idNumber`}
                    label="Number"
                    helperText="eg. 435678986545"
                    error={
                      isValidDocument({
                        documentKind: d?.kycType?.name,
                        documentNumber: d?.idNumber,
                        pan: pan,
                        state: state?.address?.state,
                      }) || ""
                    }
                  />
                </div>
                <div className="col-span-3">
                  <FileUpload label="Photo" path={`kycDetails.${idx}.photo`} />
                </div>
                <div className="col-span-1 flex justify-center items-center">
                  <CustomButton
                    className="mb-4"
                    icon="trash"
                    intent="danger"
                    minimal
                    onClick={e => {
                      setState(prev => {
                        const kycToRemove = prev?.kycDetails?.[idx]
                        return {
                          ...prev,
                          removeKycIds: kycToRemove?.id
                            ? [...(prev?.removeKycIds ?? []), kycToRemove.id]
                            : prev?.removeKycIds ?? [],
                          kycDetails: remove(prev?.kycDetails, idx),
                        }
                      })
                    }}
                  />
                </div>
              </div>
            )
          })}

          {process.env.NODE_ENV === "production" ? null : (
            <>
              <h6 className="mt-4 mb-0 vt-section-header">Assigned to route</h6>
              <p className="text-gray-600 text-sm mb-2">
                This vendor will get assigned to the selected route
              </p>
              <hr className="mt-0" />
              <div className="grid grid-cols-4 gap-4">
                <DynamicSelector
                  big
                  query={DISTRIBUTION_CENTERS}
                  path="distributionCenter"
                  label="Distribution Center"
                />
                <DynamicSelector
                  big
                  query={DC_SURVEY_AREAS}
                  variables={{
                    dcId: state?.distributionCenter?.id,
                  }}
                  name="properties.name"
                  searchVector={["properties.name"]}
                  path="surveyArea"
                  label="Survey Area"
                />
                <DynamicSelector
                  big
                  query={ROUTE_CATEGORIES}
                  path="routeCategory"
                  label="Route Category"
                />
                <DynamicMultiSelector
                  big
                  fill
                  query={ROUTES}
                  variables={{
                    filter: {
                      surveyAreaId: state?.surveyArea?.id,
                      categoryIds: [state?.routeCategory?.id],
                    },
                  }}
                  path="routes"
                  label="Routes"
                />
              </div>
            </>
          )}
        </FormController>
        {process.env.NODE_ENV === "production" ? null : (
          <OutletsMap
            items={state?.routes}
            vendorLocation={{
              lat: state?.address?.latitude,
              lng: state?.address?.longitude,
            }}
          />
        )}
        <MutationOptions
          className="mt-3"
          disabled={state?.isBlocked}
          validate={() => {
            const docIsValid = isInValidDocumentPresent({
              documents: state?.kycDetails,
              state: state?.address?.state,
            })

            if (docIsValid !== undefined && !docIsValid) {
              ErrorToast("Please resolve all errors before proceeding")
              return false
            }

            // In update mode, credit limit must not go below already-used amount
            if (id && id !== "new" && state?.credit?.totalAmount) {
              const newLimit = parseFloat(state.credit.totalAmount) || 0
              const usedAmount = parseFloat(state.credit.usedAmount) || 0
              if (newLimit < usedAmount) {
                ErrorToast(
                  `Credit limit (${newLimit}) cannot be less than the credit already used (${usedAmount})`
                )
                return false
              }
            }

            if (errors.latest) {
              ErrorToast(errors?.latest)
              return false
            }
            return true
          }}
          refetchQueries={[
            {
              query: VENDOR_LIST,
              variables: {
                query: "",
                groupName: state?.groups?.[0]?.name,
                first: 13,
                skip: 0,
              },
            },
          ]}
          onSave={async ({ addUpdateVendor: auv }) => {
            const user = auv.user
            await saveCreditIfNeeded(user.id)
            SuccessToast(`Vendor ${user.fullName} added successfully`)
            onComplete ? onComplete?.(auv) : navigate(-1)
          }}
          onSaveAndAdd={async ({ addUpdateVendor: auv }) => {
            const user = auv.user
            await saveCreditIfNeeded(user.id)
            SuccessToast(`Vendor ${user.fullName} added successfully`)
            onComplete?.(auv)
            setState({})
            navigate("/d/registration/vendors/details/new")
          }}
          onSaveAndEdit={async ({ addUpdateVendor: auv }) => {
            const user = auv.user
            await saveCreditIfNeeded(user.id)
            SuccessToast(`Vendor ${user.fullName} updated successfully`)
            onComplete?.(auv)
            if (!parseInt(id)) {
              navigate(`/d/registration/vendors/details/${user.id}`, {
                replace: true,
              })
            } else {
              refetch()
            }
          }}
          mutation={ADD_UPDATE_VENDOR}
          variables={parsedVariables}
        />
      </FormLayout>
    </>
  )
}

export default VendorRegistration
